/**
 * useHashScroll — the shared hash-scroll primitive (Soak #2 Wave A, G4).
 *
 * Three surfaces already EMIT `#note-<id>` / `#risk-flags` links
 * (ProvenanceChip, the Overview latest-activity rows, the AttentionBand,
 * and the top-bar risk line) but nothing ever CONSUMED them: the target
 * tab opened at its top and the anchor was ignored. This hook is the
 * consumer.
 *
 * On mount and whenever `location.hash` (or a caller-supplied dependency)
 * changes, and while `active` is true, it finds the element whose id
 * matches the hash and scrolls it into view. The target frequently mounts
 * AFTER the effect first runs (the tab's aggregate/notes read resolves
 * async), so it retries across a few animation frames and re-runs when the
 * caller passes freshly loaded data through `deps` — e.g. the NotesFeed
 * passes its loaded notes so a paginate-to-target lands the scroll once
 * the right page arrives.
 *
 * `active` gates it so a tab that is not the visible one never steals the
 * viewport (each tab body mounts its own useHashScroll).
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/** ~20 frames (~300ms) of retry while the anchor mounts, then give up. */
const MAX_FRAMES = 20;

export function useHashScroll(
  active = true,
  deps: readonly unknown[] = [],
  /** Scroll alignment. 'center' (default) suits point targets like a
   *  note row; the Care record's card anchors pass 'start' so a card
   *  TALLER than the viewport lands with its header in view (the card
   *  sections carry scroll-margin so the sticky chrome never occludes
   *  the jumped-to header). */
  block: ScrollLogicalPosition = 'center',
): void {
  const { hash, key: locationKey } = useLocation();
  // Scroll AT MOST ONCE per NAVIGATION (location.key ties the seek to the
  // history entry): `deps` re-run the seek only while the anchor is still
  // absent (the target page/tab may mount async). Once getElementById
  // succeeds for the current navigation we stop, so a later cache change
  // (e.g. a CM editing another note, which re-identities the notes array)
  // never yanks the viewport back to the deep-linked anchor. Keying on
  // location.key rather than the bare hash means RE-CLICKING the same
  // jump pill after scrolling away jumps again — every click is a new
  // history entry even when the hash string is unchanged.
  const satisfiedRef = useRef<string | null>(null);
  const seekToken = `${locationKey}|${hash}`;
  useEffect(() => {
    if (!active || hash.length <= 1) return undefined;
    if (satisfiedRef.current === seekToken) return undefined;
    const id = decodeURIComponent(hash.slice(1));
    let frame = 0;
    let raf = 0;
    const tryScroll = (): void => {
      if (satisfiedRef.current === seekToken) return;
      const el = document.getElementById(id);
      if (el) {
        satisfiedRef.current = seekToken; // found — scroll once, then stop
        el.scrollIntoView({ behavior: 'smooth', block });
        return;
      }
      if (frame++ < MAX_FRAMES) raf = requestAnimationFrame(tryScroll);
    };
    tryScroll();
    return () => cancelAnimationFrame(raf);
    // The caller's `deps` re-run the seek WHILE the anchor is still absent.
  }, [seekToken, active, block, ...deps]);
}
